import _ from 'lodash'
import { languagesFonts } from './v16.languages'
import { studioFonts } from './fonts.helveticas'
import { madeforFonts } from './fonts.madefor.v27'
import { googleFonts } from './fonts.google'
import { morisawaFonts_22 } from './fonts.morisawa.22'
import { morisawaFonts_44 } from './fonts.morisawa.44'

import { patchFontDisplay, parseFontFaces } from './fontsParser'
import { FontLoadStrategy, MorisawaFonts } from '@wix/thunderbolt-becky-types'

export const getMadeforFontFamillies = () => Object.keys(madeforFonts)

const DEFAULT_FONT_DISPLAY = 'block'
type FontFacesMap = Record<string, Array<string>>
const basicFontFacesMap: FontFacesMap = {
	...googleFonts,
	...languagesFonts,
	...studioFonts,
	...madeforFonts,
}

const fontFacesMap_byFontDisplay: Partial<Record<FontLoadStrategy, FontFacesMap>> = {}

let parsedMorisawa22Fonts: Record<string, Array<string>> | undefined
let parsedMorisawa44Fonts: Record<string, Array<string>> | undefined

const CONSTANTS = {
	UPLOADED_FONT_PREFIX: 'wf_',
	LONG_UPLOADED_FONT_PREFIX: 'wfont_',
}

const UPLOADED_FONT_MASK = 'wfont_[0-9a-f]{6}_[0-9a-f]{32}'
const UPLOADED_FONT_TEST = new RegExp(`^${UPLOADED_FONT_MASK}`)

export function isUploadedFontFamily(fontFamilyStr: string) {
	if (_.isNil(fontFamilyStr) || _.isEmpty(fontFamilyStr)) {
		return false
	}

	const fontName = _.head(fontFamilyStr.split(','))!
	return UPLOADED_FONT_TEST.test(fontName)
}

function getShortUploadedFontFamily(font: string) {
	const newLocal = /^wfont_[0-9a-f]{6}_([0-9a-f]{25})[0-9a-f]{7}/.exec(font)!
	return `wf_${newLocal[1]}`
}

function getUploadedId(uploadedFonStr: string) {
	return uploadedFonStr.replace(CONSTANTS.LONG_UPLOADED_FONT_PREFIX, '').trim()
}

function getFontName(font: string) {
	return _.head(font.split(',')) || ''
}

function resolveMediaRootUrl(mediaRootUrl: string): string {
	return mediaRootUrl.replace(/(https?:\/\/)|^/, 'https://').replace(/\/$|$/, '/')
}

function getFontUrl(mediaRootUrl: string, fontId: string, type: string) {
	return `${mediaRootUrl}ufonts/${fontId}/${type}/file.${type}`
}

function getSrcUrl(mediaRootUrl: string, fontId: string, type: string) {
	return `url("${getFontUrl(mediaRootUrl, fontId, type)}") format("${type}")`
}

function getUploadFontFace(font: string, mediaRootUrl: string, fontDisplay: string) {
	const fontName = getFontName(font)
	const fontId = getUploadedId(fontName)
	const shortFontName = getShortUploadedFontFamily(fontName)
	return `@font-face {
font-display: ${fontDisplay};
font-family: ${shortFontName};
src: ${getSrcUrl(mediaRootUrl, fontId, 'woff2')},
${getSrcUrl(mediaRootUrl, fontId, 'woff')},
${getSrcUrl(mediaRootUrl, fontId, 'ttf')};
}`
}

function getMorisawaFonts(morisawaFonts: MorisawaFonts) {
	if (morisawaFonts === '22') {
		parsedMorisawa22Fonts = parsedMorisawa22Fonts || parseFontFaces(morisawaFonts_22)
		return parsedMorisawa22Fonts
	} else {
		parsedMorisawa44Fonts = parsedMorisawa44Fonts || parseFontFaces(morisawaFonts_44)
		return parsedMorisawa44Fonts
	}
}

function getFontFacesMap(morisawaFonts?: MorisawaFonts) {
	return morisawaFonts ? { ...basicFontFacesMap, ...{ ...getMorisawaFonts(morisawaFonts) } } : basicFontFacesMap
}

function getFontFacesCssMap(fontDisplay: FontLoadStrategy, fontFacesMap: FontFacesMap, morisawaFonts?: MorisawaFonts) {
	if (fontDisplay === 'block') {
		return fontFacesMap
	}

	const fixedFontDisplay = (morisawaFonts ? `${fontDisplay}-${morisawaFonts}` : fontDisplay) as FontLoadStrategy
	fontFacesMap_byFontDisplay[fixedFontDisplay] =
		fontFacesMap_byFontDisplay[fixedFontDisplay] || patchFontDisplay(fontDisplay, fontFacesMap)

	return fontFacesMap_byFontDisplay[fixedFontDisplay]
}

export const getFontFacesCss = (
	fontFamilies: Array<string>,
	mediaRootUrl: string,
	fontDisplay: FontLoadStrategy = DEFAULT_FONT_DISPLAY,
	morisawaFonts?: MorisawaFonts
): string => {
	const fontFacesMap = getFontFacesMap(morisawaFonts)
	const fontFacesCssMap = getFontFacesCssMap(fontDisplay, fontFacesMap, morisawaFonts)

	mediaRootUrl = resolveMediaRootUrl(mediaRootUrl)
	const fonts = fontFamilies.reduce<Array<string>>((resultArray, fontFamily) => {
		const isUploadedFont = isUploadedFontFamily(fontFamily)
		const fontFaces = isUploadedFont
			? getUploadFontFace(fontFamily, mediaRootUrl, fontDisplay)
			: fontFacesCssMap![fontFamily.toLowerCase()]
		return resultArray.concat(fontFaces)
	}, [])
	const fontsCss = _.compact(fonts).join('\n')
	return fontsCss
}

export const determineFontFacesFontDisplay = ({
	isEditor,
	isWixSite,
	wixSitesFontDisplaySwapExperiment,
	siteFontDisplay,
}: {
	isEditor: boolean
	isWixSite: boolean
	wixSitesFontDisplaySwapExperiment: boolean
	siteFontDisplay: FontLoadStrategy | undefined
}): FontLoadStrategy => {
	if (isEditor) {
		// in order to hasPendingFonts ViewerAPI to work as expected, the font display should be block
		return 'block'
	}
	const defaultFontDisplay = wixSitesFontDisplaySwapExperiment && isWixSite ? 'swap' : 'block'
	return siteFontDisplay || (defaultFontDisplay as FontLoadStrategy)
}
